/**
 * PinnedSection — renders pinned tasks at the top of a container view.
 *
 * Appears above the main task list in project/list views when there are
 * pinned tasks for the current container. Tasks are rendered as compact
 * items with a pin indicator.
 *
 * This component also exports `usePinnedIds` hook for the parent to
 * filter pinned tasks out of the main flow.
 */

import { memo, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db } from "@/db/schema"
import { unpinTask } from "@/db/operations"
import { ObjectStatus } from "@/db/schema"
import PinIcon from "@/shared/icons/pikaicons/stroke/general/pin-slant.svg?react"
import type { DbObject } from "@/db/schema"

interface PinnedSectionProps {
  containerId: string
  onOpenTask: (id: string) => void
  activeTaskId: string | null
}

/**
 * Hook to get pinned task IDs for a container (for filtering the main list).
 */
export function usePinnedIds(containerId: string | null): Set<string> {
  const [entries] = useLiveQueryImmediate(
    () => {
      if (!containerId) return []
      const prefix = `#pin:${containerId}:`
      return db.setting
        .where("id")
        .between(prefix, prefix + "\uffff", true, true)
        .toArray()
    },
    [containerId],
    undefined,
  )

  return useMemo(() => {
    if (!entries || entries.length === 0) return new Set<string>()
    return new Set(
      entries.map((e) => {
        const id = e.id!
        const prefix = `#pin:${containerId}:`
        return id.slice(prefix.length)
      }),
    )
  }, [entries, containerId])
}

export const PinnedSection = memo(function PinnedSection({
  containerId,
  onOpenTask,
  activeTaskId,
}: PinnedSectionProps) {
  const { t } = useTranslation()
  const prefix = `#pin:${containerId}:`

  const [entries] = useLiveQueryImmediate(
    () =>
      db.setting
        .where("id")
        .between(prefix, prefix + "\uffff", true, true)
        .toArray(),
    [prefix],
    undefined,
  )

  // Fetch actual tasks for pinned entries
  const taskIds = useMemo(
    () => (entries ?? []).map((e) => e.id!.slice(prefix.length)),
    [entries, prefix],
  )

  const [pinnedTasks] = useLiveQueryImmediate(
    async () => {
      if (taskIds.length === 0) return []
      const tasks = await Promise.all(taskIds.map((id) => db.totodo.get(id)))
      // Sort by entry value (pinnedAt / reorder position)
      const entriesSorted = [...(entries ?? [])].sort(
        (a, b) => Number(a.value) - Number(b.value),
      )
      const orderedIds = entriesSorted.map((e) => e.id!.slice(prefix.length))
      return orderedIds
        .map((id) => tasks.find((t) => t?.id === id))
        .filter(
          (t): t is DbObject =>
            t != null &&
            t.status === ObjectStatus.Unfinished &&
            !t.isDeleted,
        )
    },
    [taskIds, entries],
    undefined,
  )

  if (!pinnedTasks || pinnedTasks.length === 0) return null

  return (
    <div className="mx-2 mb-1">
      {/* Pinned header */}
      <div className="flex items-center gap-1.5 px-2 pb-1 pt-0.5 text-xs font-medium text-muted-foreground">
        <PinIcon className="size-3" />
        <span>{t("Pinned")}</span>
      </div>

      {/* Pinned tasks */}
      <div className="flex flex-col">
        {pinnedTasks.map((task) => (
          <PinnedTaskItem
            key={task.id}
            task={task}
            containerId={containerId}
            isActive={task.id === activeTaskId}
            onOpen={onOpenTask}
          />
        ))}
      </div>

      {/* Subtle divider */}
      <div className="mx-2 mt-1 h-px bg-foreground/5" />
    </div>
  )
})

// ---------------------------------------------------------------------------
// Individual pinned task item
// ---------------------------------------------------------------------------

const PinnedTaskItem = memo(function PinnedTaskItem({
  task,
  containerId,
  isActive,
  onOpen,
}: {
  task: DbObject
  containerId: string
  isActive: boolean
  onOpen: (id: string) => void
}) {
  const handleClick = useCallback(() => {
    if (task.id) onOpen(task.id)
  }, [task.id, onOpen])

  const handleUnpin = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (task.id) void unpinTask(containerId, task.id)
    },
    [task.id, containerId],
  )

  return (
    <button
      className={`group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted/50 ${isActive ? "bg-muted/50" : ""}`}
      onClick={handleClick}
    >
      <PinIcon className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate">{task.title || "Untitled"}</span>
      <span
        className="shrink-0 rounded-md px-1 py-0.5 text-xs text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:bg-foreground/10"
        onClick={handleUnpin}
        role="button"
        tabIndex={-1}
      >
        Unpin
      </span>
    </button>
  )
})
